import { Component, ErrorInfo, ReactNode } from 'react';
import { ServerError } from 'components/errorPages/ServerError';
import { reportError } from 'services/observability';

interface ErrorBoundaryProps {
    children: ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * Catches render errors anywhere below it and reports them to Coralogix, then
 * shows the ServerError fallback instead of a blank screen.
 *
 * This is a class component by necessity: React error boundaries require
 * `getDerivedStateFromError` / `componentDidCatch`, which have no hooks
 * equivalent (exception to the functional-only component rule).
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = { hasError: false };

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo): void {
        reportError(error.message, {
            stack: error.stack,
            componentStack: info.componentStack ?? undefined,
            route: typeof window !== 'undefined' ? window.location.pathname : undefined,
        });
    }

    render(): ReactNode {
        if (this.state.hasError) {
            return <ServerError />;
        }
        return this.props.children;
    }
}
